// hooks/useFinancialHealth.js
import { useQuery } from '@tanstack/react-query';
import { reactQueryFinanceDefaults } from './queryDefaults';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

/**
 * Hook for Financial Health Metrics with YTD vs Full Last Year Comparison
 * 
 * The backend now automatically:
 * - Fetches YTD data for current period
 * - Fetches Full Year data for previous year
 * - Calculates all YoY comparisons
 * 
 * No need for separate API calls or manual date calculations!
 */

// Helper function to calculate average head count across all months
function calculateAverageHeadCount(monthlyData) {
  if (!monthlyData || monthlyData.length === 0) return 0;
  
  const total = monthlyData.reduce((sum, month) => sum + (month.active_employees || 0), 0);
  return Math.round(total / monthlyData.length); // Round to nearest whole number
}

const useFinancialHealth = (dateRange) => {
  const fetchFinancialHealth = async () => {
    if (!dateRange?.start_date || !dateRange?.end_date) {
      throw new Error('Date range is required');
    }

    // ✅ Single API call - backend handles everything!
    const [healthData, debtData, agiData] = await Promise.all([
      // Financial health with YTD vs Last Year comparison built-in
      apiService.get(ENDPOINTS.FINANCE_FINANCIAL_HEALTH, {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      }),
      // Debt (separate - no YoY comparison needed)
      apiService.get(ENDPOINTS.FINANCE_DEBT_OUTSTANDING),
      // AGI (separate - for backward compatibility)
      apiService.get(ENDPOINTS.FINANCE_ADJUSTED_GROSS_INCOME, {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      }),
    ]);

    // Extract current period and previous period data from backend response
    const currentPeriod = healthData?.current_period?.metrics || {};
    const previousPeriod = healthData?.previous_period?.metrics || {};
    
    // Build monthly data structure for current period (YTD)
    const monthlyData = {
      profitMargin: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.profit_margin_pct || 0,
      })),
      netIncome: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.net_income || 0,
      })),
      grossProfit: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.gross_profit || 0,
      })),
      agi: (agiData?.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.adjusted_gross_income || 0,
      })),
      avgRetainerRevenue: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.retainer_revenue || 0,
      })),
      headCount: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.active_employees || 0,
      })),
      laborMargin: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.labor_costs_pct || 0,
      })),
      revPerFTE: (currentPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.revenue_per_fte || 0,
      })),
      employeeSatisfaction: [],
    };

    // Build monthly data structure for previous period (Full Last Year)
    const lastYearMonthlyData = {
      profitMargin: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.profit_margin_pct || 0,
      })),
      netIncome: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.net_income || 0,
      })),
      grossProfit: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.gross_profit || 0,
      })),
      agi: [], // AGI not available for previous year yet - could add if needed
      avgRetainerRevenue: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.retainer_revenue || 0,
      })),
      headCount: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.active_employees || 0,
      })),
      laborMargin: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.labor_costs_pct || 0,
      })),
      revPerFTE: (previousPeriod.monthly_data || []).map(m => ({
        period_label: m.period_label,
        value: m.revenue_per_fte || 0,
      })),
      employeeSatisfaction: [],
    };

    const currentDebt = debtData?.total_debt || 0;

    // ✅ Use backend-calculated metrics (already overall totals/percentages)
    const metrics = {
      profitMargin: currentPeriod.overall_profit_margin_pct || 0,
      netIncome: currentPeriod.total_net_income || 0,
      grossProfit: currentPeriod.total_gross_profit || 0,
      agi: agiData?.summary?.total_agi || 0,
      avgRetainerRevenue: currentPeriod.total_retainer_revenue || 0,
      debtOutstanding: currentDebt,
      headCount: currentPeriod.current_employees || 0,  // Most recent month
      laborMargin: currentPeriod.overall_labor_margin_pct || 0,
      revPerFTE: currentPeriod.overall_revenue_per_fte || 0,
      employeeSatisfaction: 0,
    };

    const previousMetrics = {
      profitMargin: previousPeriod.overall_profit_margin_pct || 0,
      netIncome: previousPeriod.total_net_income || 0,
      grossProfit: previousPeriod.total_gross_profit || 0,
      agi: previousPeriod.total_gross_profit || 0,
      avgRetainerRevenue: previousPeriod.total_retainer_revenue || 0,
      debtOutstanding: null, // No comparison for debt
      headCount: calculateAverageHeadCount(previousPeriod.monthly_data || []), // Average of all 2024 months
      laborMargin: previousPeriod.overall_labor_margin_pct || 0,
      revPerFTE: previousPeriod.overall_revenue_per_fte || 0,
      employeeSatisfaction: 0,
    };


    // ✅ Add period information for display labels
    const periodInfo = {
      current: {
        start_date: healthData?.current_period?.start_date,
        end_date: healthData?.current_period?.end_date,
        is_ytd: healthData?.current_period?.is_ytd || false,
      },
      previous: {
        start_date: healthData?.previous_period?.start_date,
        end_date: healthData?.previous_period?.end_date,
        is_full_year: healthData?.previous_period?.is_full_year || false,
      },
      comparison_type: healthData?.metadata?.comparison_type || 'YTD vs Full Previous Year',
    };

    return {
      metrics,
      previousMetrics,
      monthlyData,
      lastYearMonthlyData,
      periodInfo,  // ✅ NEW: For display labels
      lastUpdated: new Date().toISOString(),
    };
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['financialHealth', dateRange?.start_date, dateRange?.end_date],
    queryFn: fetchFinancialHealth,
    ...reactQueryFinanceDefaults,
    enabled: !!dateRange?.start_date && !!dateRange?.end_date,
  });

  return {
    // Financial health data
    metrics: data?.metrics || {
      profitMargin: 0,
      netIncome: 0,
      grossProfit: 0,
      agi: 0,
      avgRetainerRevenue: 0,
      debtOutstanding: 0,
      headCount: 0,
      laborMargin: 0,
      revPerFTE: 0,
      employeeSatisfaction: 0,
    },
    previousMetrics: data?.previousMetrics || {
      profitMargin: 0,
      netIncome: 0,
      grossProfit: 0,
      agi: 0,
      avgRetainerRevenue: 0,
      debtOutstanding: 0,
      headCount: 0,
      laborMargin: 0,
      revPerFTE: 0,
      employeeSatisfaction: 0,
    },
    monthlyData: data?.monthlyData || {
      profitMargin: [],
      netIncome: [],
      grossProfit: [],
      agi: [],
      avgRetainerRevenue: [],
      headCount: [],
      laborMargin: [],
      revPerFTE: [],
      employeeSatisfaction: [],
    },
    lastYearMonthlyData: data?.lastYearMonthlyData || {
      profitMargin: [],
      netIncome: [],
      grossProfit: [],
      agi: [],
      avgRetainerRevenue: [],
      headCount: [],
      laborMargin: [],
      revPerFTE: [],
      employeeSatisfaction: [],
    },
    periodInfo: data?.periodInfo || {
      current: {
        start_date: null,
        end_date: null,
        is_ytd: false,
      },
      previous: {
        start_date: null,
        end_date: null,
        is_full_year: false,
      },
      comparison_type: 'YTD vs Full Previous Year',
    },
    lastUpdated: data?.lastUpdated || null,

    // Loading states
    loading: isLoading,
    refreshing: isFetching,

    // Error handling
    error: isError ? (error?.message || 'Failed to load financial health data') : null,
    isError,

    // Status
    initialized: !!data,

    // Actions
    refetch,
  };
};

export { useFinancialHealth };